import { pageURLs } from "@/constants";
import { TOUR_NETWORK_SLUG, TOUR_SAMPLE_SLUG } from "./seeds";

const ROW = ".horizontal-tab-wrap tbody tr:first-child";
const SAMPLE_PAGE = `${pageURLs.wantAdDetails}/${TOUR_SAMPLE_SLUG}`;
const NETWORK_PAGE = `${pageURLs.wantAdDetails}/${TOUR_NETWORK_SLUG}`;

export const SECTION_TITLES = {
  feed: "The Feed",
  buy: "Buy side",
  sell: "Sell side",
  connect: "Connect",
};

const compactBudget = (value) => {
  const amount = Number(value) || 0;
  if (amount >= 999500) return `$${(amount / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  const thousands = Math.round(amount / 1000);
  return thousands > 0 ? `$${thousands}K` : "";
};

export const oldWayOf = (samples) => {
  const { bedrooms, budgetMax } = samples?.wantListing || {};
  const city = samples?.market?.city;
  const beds = bedrooms || 3;
  return [
    {
      channel: "Facebook group",
      message: `ISO ${beds} bed${city ? ` near ${city}` : ""}${compactBudget(budgetMax) ? ` under ${compactBudget(budgetMax)}` : ""}, DM me!`,
      cost: "Not searchable, not matched, buried by tomorrow",
    },
    {
      channel: "Email thread",
      message: "RE: RE: FW: Buyer needs this week",
      cost: "Not searchable, not matched, half the office missed it",
    },
    {
      channel: "Office meeting",
      message: "Tuesday wants and needs roundtable",
      cost: "No record, not matched, only the room heard it",
    },
    {
      channel: "Group text",
      message: `Anyone have a ${beds}-bed coming up?`,
      cost: "No record, not matched, no follow-up",
    },
  ];
};

export const OLD_WAY = [
  "Buyer needs scattered across Facebook groups, email threads, office meetings and group texts",
  "Nothing is matched to for-sale listings, and most of it is gone by tomorrow",
  "Only the agents in that group or that room ever hear about the buyer",
];

export const NEW_WAY = (network) => [
  `Every ${network} member on one network, with Buyer Want-Listings broadcast 24/7`,
  "Every Buyer Want-Listing is matched to for-sale listings the moment it is published",
  "Each new member and Buyer Want-Listing makes the network stronger for everyone",
  "No client information shared. Messages and introductions stay in one place",
];

export const FOCUS_CHOICES = [
  { id: "buy", title: "Buyer clients" },
  { id: "sell", title: "Seller clients" },
  { id: "both", title: "Both" },
];

export const buildSteps = ({ network = "MLS", focus = null, mlsSync = false } = {}) => {
  const feed = [
    {
      kind: "step",
      section: "feed",
      page: pageURLs.home,
      target: "[data-tour='nav-feed']",
      title: "Real-time buyer demand",
      body: `The Feed shows every Buyer Want-Listing published by ${network} members. Your MLS has always shown the properties for sale. Now it shows **Buyer Want-Listings** too, so you see both sides, 24/7.`,
    },
  ];

  const buy = [
    {
      kind: "cut",
      section: "buy",
      headline: "How Buyer Agents Use\nThe Buyer Listing\u00a0Service®",
      visual: "page",
      points: [
        { icon: "listing", title: "What a Buyer Want-Listing is", line: "A listing for what a buyer client wants. Like a for-sale listing, but for the buy side, built on a formal protocol." },
        { icon: "broadcast", title: `Broadcast to your ${network} network`, line: "For the first time, buyer demand is seen by every member, 24/7, with optional syndication to Gitcha.com." },
        { icon: "service", title: "A new buyer agency service", line: "Real exposure for every Buyer Want-Listing you publish." },
      ],
      note: "No client information is ever shared.",
    },
    {
      kind: "step",
      section: "buy",
      page: pageURLs.agentMyWantAdListings,
      target: "[data-tour='nav-want-listings']",
      title: "My Want-Listings",
      body: "Every Buyer Want-Listing you publish lives here, with its matches and introductions. The sample on this page was built from today's market so you can see one.",
    },
    {
      kind: "step",
      section: "buy",
      page: SAMPLE_PAGE,
      target: "[data-member-tour='wl-title']",
      title: "A clear title",
      body: "Say what the buyer client needs in a few words, like \u201cLooking for a 4-bed with a big backyard.\u201d It's the first line other agents read in the Feed.",
    },
    {
      kind: "step",
      section: "buy",
      page: SAMPLE_PAGE,
      target: "[data-member-tour='wl-location']",
      title: "Pinpoint the location",
      body: "Choose the way that fits the buyer client: a radius, ZIP codes, or an area you draw on the map.",
      extra: "location-options",
    },
    {
      kind: "step",
      section: "buy",
      page: SAMPLE_PAGE,
      target: "[data-member-tour='wl-must-haves']",
      title: "Must-haves",
      body: "Budget, beds, baths and size: what a property has to have to be a fit.",
    },
    {
      kind: "step",
      section: "buy",
      page: SAMPLE_PAGE,
      target: "[data-member-tour='wl-nice-to-haves']",
      title: "Nice-to-haves",
      body: "Pick everything that would be a plus: a pool, a view, an office, a shop, a big garage, a community pool and more. You can choose up to 25 across lot, room and space, exterior, interior, architectural style and community amenities. **The more you add, the better we can match it across your network.**",
    },
    {
      kind: "step",
      section: "buy",
      page: SAMPLE_PAGE,
      target: "[data-member-tour='wl-description']",
      title: "Tell the full story",
      body: "Write it like you're listing a property for sale, for the buy side. Paint the picture: **the area, the lifestyle and every detail that matters**, plus **deal specifics like a rate buydown, closing timeline or compensation**. Fair housing guardrails keep it compliant as you write.",
    },
    {
      kind: "step",
      section: "buy",
      page: SAMPLE_PAGE,
      target: "[data-member-tour='wl-details']",
      title: "The details matter",
      body: "Check the box that a buyer agreement is in place, then add the timeline, financing, purchase type, preferred neighborhoods and school districts. Together they show listing agents a serious, ready buyer. No client information is ever shared.",
    },
    {
      kind: "step",
      section: "buy",
      page: SAMPLE_PAGE,
      target: "[data-rr-ui-event-key='Matches']",
      title: "Beyond broadcasting",
      body: `Agents already share buyer needs in Facebook groups, texts and office meetings. Here, every Buyer Want-Listing is also matched against active and coming-soon for-sale listings in the ${network}, and each match is scored.`,
    },
    {
      kind: "step",
      section: "buy",
      page: SAMPLE_PAGE,
      target: "[data-member-tour='share']",
      title: "Maximum exposure",
      body: "Syndicate any Buyer Want-Listing to Gitcha.com and share it like a for-sale listing, on social, by email or by text. **More exposure brings inbound calls, like sign calls on a listing, and more transactional opportunities.** No client information is ever shared.",
      link: "gitcha",
    },
  ];

  const sell = [
    {
      kind: "cut",
      section: "sell",
      headline: "How Selling Agents Use\nThe Buyer Listing\u00a0Service®",
      visual: "demand",
      points: [
        { icon: "demand", title: "Demand insights", line: "See how many Buyer Want-Listings match any property, even before it's listed." },
        { icon: "price", title: "Demand insights at every price point", line: "See where buyer budgets land before you set a price." },
        { icon: "report", title: "A Buyer Match Report to pair with your CMA", line: "Bring demand insights to your next listing presentation." },
      ],
    },
    {
      kind: "step",
      section: "sell",
      page: pageURLs.agentInventoryListing,
      target: "[data-tour='nav-properties']",
      title: "My Properties",
      body: "Every property you're working with on the sell side lives here, each with its demand insights.",
    },
    ...(mlsSync
      ? [
          {
            kind: "step",
            section: "sell",
            page: pageURLs.agentInventoryListing,
            target: ".underline-tabs[aria-label='Properties']",
            title: "Two kinds of properties",
            body: `**My Active MLS Listings** sync automatically from the ${network} MLS, so there's nothing to enter twice. **Prospects Inventory** is for properties you don't have listed yet: past clients' purchases (we add these from your MLS records), your sphere, expired and withdrawn listings, for-sale-by-owners, investors weighing a sale, and the seller you're meeting next week.`,
          },
        ]
      : []),
    {
      kind: "step",
      section: "sell",
      page: pageURLs.agentInventoryListing,
      target: ROW,
      title: "A prospect's property",
      body: "Save any prospect's property before it's listed and **see the buyer demand before the listing appointment**, so you walk in knowing who's already looking.",
    },
    {
      kind: "step",
      section: "sell",
      page: pageURLs.agentInventoryListing,
      target: `${ROW} td:nth-child(3)`,
      title: "Not reverse prospecting",
      body: "Reverse prospecting only works once a property is in the MLS. Here, **every Buyer Want-Listing that matches is counted live, listed or not.**",
      extra: "reverse-compare",
    },
    {
      kind: "step",
      section: "sell",
      page: pageURLs.home,
      target: "[data-member-tour='sell-demand']",
      title: "Demand insights",
      body: "Pick a property on the Feed and its demand insights open here: **how many Buyer Want-Listings match it and where their budgets cluster.**",
    },
    {
      kind: "step",
      section: "sell",
      page: pageURLs.home,
      target: "[data-member-tour='sell-demand'] .single-price-slider",
      title: "Try a list price",
      body: "Drag the slider to a price and see **how many buyer budgets it reaches.** It's the fastest way to show a seller what a price change does.",
    },
    {
      kind: "step",
      section: "sell",
      page: pageURLs.home,
      target: "[data-member-tour='sell-demand'] [data-tour='download-report-match']",
      title: "Buyer Match Report",
      body: "Download the report and **pair it with your CMA** at the listing appointment, for pricing and go-to-market conversations.",
    },
  ];

  const connect = [
    {
      kind: "cut",
      section: "connect",
      headline: "Collaboration Between Agents",
      visual: "connect",
      points: [
        { icon: "message", title: "Ask the agent directly", line: "Questions go straight to the agent behind any Buyer Want-Listing." },
        { icon: "send", title: "Send an introduction", line: "Put your for-sale listing in front of the buyer's agent." },
        { icon: "shield", title: "Collaborate compliantly", line: "Compliance guardrails are built into every conversation. No client information is ever shared." },
      ],
    },
    {
      kind: "step",
      section: "connect",
      page: NETWORK_PAGE,
      target: "[data-member-tour='message']",
      title: "Ask the buyer's agent directly",
      body: "Have a question about a Buyer Want-Listing? Message the agent behind it right from the page. **Direct, efficient and compliant**: every conversation stays in one place with compliance guardrails built in, and no client information is ever shared.",
    },
    {
      kind: "step",
      section: "connect",
      page: NETWORK_PAGE,
      target: "[data-member-tour='send-intro']",
      title: "Send your listing as an introduction",
      body: "Have an active for-sale listing that fits? **Send it through the introduction tool**: the buyer's agent sees the property next to their Buyer Want-Listing, and the whole conversation stays in one place from the first message.",
    },
  ];

  const sides = focus === "sell" ? [...sell, ...buy] : [...buy, ...sell];
  return [...feed, ...sides, ...connect, { kind: "compare", section: "connect" }];
};

export const seedSectionOf = (steps, index) => {
  const item = steps[index];
  if (!item) return null;
  if (item.kind !== "cut") return item.section;
  const previous = steps[index - 1];
  return previous ? previous.section : null;
};

export const SECONDS_PER_SCREEN = 8;

export const tourMinutes = (steps) => Math.max(1, Math.round((steps.length * SECONDS_PER_SCREEN) / 60));

export const TOUR_MINUTES = tourMinutes(buildSteps());

export const tourLengthTitle = () => `Take the ${TOUR_MINUTES}\u2011minute\u00a0tour`;
